import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { X, ShieldCheck, Lock, Loader2, AlertTriangle } from "lucide-react";

/**
 * Stripe Checkout paywall.
 * Fetches product price from backend, then on Pay click POSTs /api/checkout/session
 * and redirects to Stripe-hosted Checkout.
 */
export default function PaywallModal({ productSlug, onClose }) {
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/checkout/products/${productSlug}`);
        if (alive) setProduct(data);
      } catch (ex) {
        if (alive) setError(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      }
    })();
    return () => { alive = false; };
  }, [productSlug]);

  useEffect(() => {
    const onEsc = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onEsc);
    return () => document.removeEventListener("keydown", onEsc);
  }, [onClose]);

  const formatINR = (amount) =>
    new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(amount);

  const pay = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/checkout/session", {
        product_slug: productSlug,
        origin_url: window.location.origin,
      });
      if (!data?.url) throw new Error("No checkout URL returned");
      window.location.href = data.url; // hard redirect to Stripe
    } catch (ex) {
      setError(formatApiErrorDetail(ex.response?.data?.detail) || ex.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-ink/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="paywall-modal">
      <div className="bg-white max-w-md w-full border border-ink-line rounded-sm" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink-line">
          <div className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-brand" />
            <div className="font-display font-bold tracking-tight">Unlock TRCA Assessment</div>
          </div>
          <button onClick={onClose} className="p-1 text-ink-muted hover:text-ink" data-testid="paywall-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.2em] text-ink-muted">One-time access</div>
            <div className="font-display text-4xl font-bold tracking-tight mt-1 text-brand" data-testid="paywall-price">
              {product ? formatINR(product.amount) : "—"}
            </div>
            <p className="text-sm text-ink-muted mt-2 leading-relaxed">
              {product?.description || "Full TRCA scoring with section breakdown, readiness band and a branded result page you can share with your team."}
            </p>
          </div>

          <ul className="text-sm space-y-2 border-t border-ink-line pt-4">
            <li className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-good mt-0.5 shrink-0" />
              Six-section audit (governance, capacity, compliance, pricing, delivery, risk).
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-good mt-0.5 shrink-0" />
              Band-level recommendation tailored to your win rate.
            </li>
            <li className="flex items-start gap-2">
              <ShieldCheck className="w-4 h-4 text-good mt-0.5 shrink-0" />
              Secure payment via Stripe · UPI, RuPay, card and netbanking.
            </li>
          </ul>

          <div className="text-[11px] text-ink-muted leading-relaxed bg-secondary border border-ink-line p-3 rounded-sm">
            <strong className="text-ink">Refund policy:</strong> No refund once the assessment is started. Cancel before starting for a full refund — contact support.
          </div>

          {error && (
            <div className="text-sm text-warn bg-warn-soft border border-warn/30 px-3 py-2 rounded-sm flex items-start gap-2" data-testid="paywall-error">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {error}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-ink-line flex items-center justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-ink-line rounded-sm hover:bg-secondary transition-colors" data-testid="paywall-cancel-btn">
            Cancel
          </button>
          <button
            onClick={pay}
            disabled={loading || !product}
            data-testid="paywall-pay-btn"
            className="px-5 py-2.5 text-sm bg-brand hover:bg-brand-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-sm flex items-center gap-2 transition-colors"
          >
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Redirecting…</> : <>Pay {product ? formatINR(product.amount) : ""} & continue</>}
          </button>
        </div>
      </div>
    </div>
  );
}
